import React, { useEffect, useRef } from 'react';

const FEATURES = [
  { icon: '⚡', title: 'Real-Time Inventory Sync', desc: 'Stock levels update instantly across all stores, POS terminals, and delivery channels — eliminating overselling and manual reconciliation forever.', tag: 'Inventory', color: '#2563eb' },
  { icon: '🚚', title: 'Integrated Delivery', desc: 'Built-in delivery management with order assignment, real-time tracking, and proof of delivery capture — no third-party logistics software needed.', tag: 'Delivery', color: '#059669' },
  { icon: '💳', title: 'Flexible Payment Modes', desc: 'Accept Cash, Online (UPI/Cards), and Cash on Delivery seamlessly. Each payment mode is reconciled automatically in your financial reports.', tag: 'Payments', color: '#7c3aed' },
  { icon: '📣', title: 'Promotions & Advertisements', desc: 'Create targeted offers, discount campaigns, and in-app advertisements to drive footfall and increase average order value across all channels.', tag: 'Marketing', color: '#d97706' },
  { icon: '🔔', title: 'Low Stock Alerts', desc: 'Automated alerts notify your team when inventory drops below reorder thresholds, ensuring you never run out of fast-moving products.', tag: 'Alerts', color: '#dc2626' },
  { icon: '🏪', title: 'Multi-Store Management', desc: 'Run a chain of stores or franchise branches from a single portal. Each location has its own configuration, staff, and inventory — all visible from HQ.', tag: 'Multi-Store', color: '#0891b2' },
  { icon: '📋', title: 'Vendor & Purchase Management', desc: 'Manage suppliers, raise purchase orders, track invoices, and view complete vendor history — all in one place with full audit trails.', tag: 'Procurement', color: '#4f46e5' },
  { icon: '🔁', title: 'Refund Support', desc: 'Handle customer refunds and returns with a structured workflow — including partial refunds, item-level returns, and payment reversal tracking.', tag: 'Returns', color: '#059669' },
  { icon: '⏰', title: 'Business Hour Configuration', desc: 'Set store operating hours, delivery windows, and order acceptance times per branch. Customers always see accurate availability in the app.', tag: 'Configuration', color: '#d97706' },
];

function FeatureCard({ feat, delay }) {
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) e.target.classList.add('visible'); }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="reveal card" style={{ padding: '28px', transitionDelay: delay + 's', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${feat.color},transparent)` }} />
      <div style={{ width: 48, height: 48, borderRadius: 12, background: feat.color + '12', border: `1px solid ${feat.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 16 }}>{feat.icon}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <h3 className="t-card" style={{ color: '#111827', flex: 1, paddingRight: 8 }}>{feat.title}</h3>
        <span style={{ background: feat.color + '12', color: feat.color, border: `1px solid ${feat.color}25`, borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.4px', whiteSpace: 'nowrap', flexShrink: 0 }}>{feat.tag}</span>
      </div>
      <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>{feat.desc}</p>
    </div>
  );
}

export default function Features() {
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) e.target.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="section" id="features" style={{ background: 'white' }}>
      <div className="container">
        <div ref={ref} className="reveal" style={{ maxWidth: 580, marginBottom: 60 }}>
          <span className="eyebrow">Platform Features</span>
          <h2 className="t-section" style={{ color: '#0c1220' }}>Every feature your retail business needs.</h2>
          <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.7, marginTop: 16 }}>
            Built from the ground up for modern Indian retail — from single kirana stores to multi-branch chains and franchise networks.
          </p>
        </div>
        <div className="g3">
          {FEATURES.map((f, i) => <FeatureCard key={f.title} feat={f} delay={(i % 3) * 0.08} />)}
        </div>
      </div>
    </section>
  );
}
